/**
 * Any Dealer-specific javascript can be added here.
 *
 * The OEM theme's JS is all bundled by mix, but if you need to include a
 * import a specific file, '@oem-theme' is an alias to the OEM theme's
 * 'assets/js' folder, and can be referenced like so:
 * import { convertStrToMilitaryTime } from '@oem-theme/utilities.js';
 *
 * For convenience, '@dealer' is an alias to this theme's 'assets/js' folder.
 */

import $ from 'jquery';

import './components/location-widget';
import { applyCardGalleries } from './components/card-gallery';

/**
 * Splits makes into "Popular Makes" and "Other Makes" using settings from
 * window.ocPopularMakesSettings (injected by functions.php).
 * Returns a Choices.js-compatible array with groups.
 */
function buildGroupedMakeChoices(makes, allMakesLabel) {
  const cfg = window.ocPopularMakesSettings || {};
  const enabled     = cfg.enabled !== false;
  const count       = cfg.count       ?? 15;   // -1 = no limit
  const minVehicles = cfg.min_vehicles ?? -1;   // -1 = no minimum
  const forcedSlugs = Array.isArray(cfg.forcedSlugs) ? cfg.forcedSlugs : [];

  const sortByName  = (a, b) => a.name.localeCompare(b.name);
  const makeToChoice = (m) => ({
    value: m.slug,
    label: m.doc_count ? `${m.name} (${m.doc_count})` : m.name,
  });

  if (!makes || makes.length === 0) {
    return [{ value: '', label: allMakesLabel }];
  }

  if (!enabled) {
    return [
      { value: '', label: allMakesLabel },
      ...[...makes].sort(sortByName).map(makeToChoice),
    ];
  }

  // Separate forced makes (always popular) from the rest
  const forcedSet   = new Set(forcedSlugs);
  const forcedMakes = makes.filter((m) => forcedSet.has(m.slug));
  const restMakes   = makes.filter((m) => !forcedSet.has(m.slug));

  // Apply min_vehicles filter to the non-forced pool
  const eligible = minVehicles > 0
    ? restMakes.filter((m) => (m.doc_count || 0) >= minVehicles)
    : restMakes;

  // Sort eligible by doc_count desc, then take top N
  const sorted  = [...eligible].sort((a, b) => (b.doc_count || 0) - (a.doc_count || 0));
  const topMakes = count >= 0 ? sorted.slice(0, count) : sorted;

  // Popular = forced + top-N (de-duplicated via forcedSet above), sorted alpha
  const popularMakes = [...forcedMakes, ...topMakes].sort(sortByName);

  // Other = everything not in popular
  const popularSlugs = new Set(popularMakes.map((m) => m.slug));
  const otherMakes   = makes.filter((m) => !popularSlugs.has(m.slug)).sort(sortByName);

  if (otherMakes.length === 0) {
    // All makes fit in one group — flat list is cleaner
    return [
      { value: '', label: allMakesLabel },
      ...popularMakes.map(makeToChoice),
    ];
  }

  return [
    { value: '', label: allMakesLabel },
    {
      label: 'Popular Makes',
      id: 1,
      disabled: false,
      choices: popularMakes.map(makeToChoice),
    },
    {
      label: 'Other Makes',
      id: 2,
      disabled: false,
      choices: otherMakes.map(makeToChoice),
    },
  ];
}

/**
 * Applies Popular / Other Makes grouping to a native <select> element on the SRP.
 * Uses the same ocPopularMakesSettings config as buildGroupedMakeChoices.
 *
 * @param {HTMLSelectElement} selectEl - The make <select> element to rewrite.
 * @param {Array}             makes    - Raw make objects from the inventory API
 *                                       (each with .slug, .name, .doc_count).
 */
function applyGroupedMakeOptgroups(selectEl, makes) {
  const cfg = window.ocPopularMakesSettings || {};
  const enabled = cfg.enabled !== false;

  if (!enabled || !makes || makes.length === 0) return;

  const count       = cfg.count       ?? 15;
  const minVehicles = cfg.min_vehicles ?? -1;
  const forcedSlugs = Array.isArray(cfg.forcedSlugs) ? cfg.forcedSlugs : [];
  const sortByName  = (a, b) => a.name.localeCompare(b.name);

  const forcedSet   = new Set(forcedSlugs);
  const forcedMakes = makes.filter((m) => forcedSet.has(m.slug));
  const restMakes   = makes.filter((m) => !forcedSet.has(m.slug));

  const eligible = minVehicles > 0
    ? restMakes.filter((m) => (m.doc_count || 0) >= minVehicles)
    : restMakes;

  const sorted   = [...eligible].sort((a, b) => (b.doc_count || 0) - (a.doc_count || 0));
  const topMakes = count >= 0 ? sorted.slice(0, count) : sorted;

  const popularMakes = [...forcedMakes, ...topMakes].sort(sortByName);
  const popularSlugs = new Set(popularMakes.map((m) => m.slug));
  const otherMakes   = makes.filter((m) => !popularSlugs.has(m.slug)).sort(sortByName);

  // When all makes fit in one group a flat list is cleaner — nothing to do.
  if (otherMakes.length === 0) return;

  const makeOption = (m) => {
    const opt = document.createElement('option');
    opt.value = m.slug;
    opt.textContent = m.doc_count ? `${m.name} (${m.doc_count})` : m.name;
    if (m.name) opt.dataset.optionName = m.name;
    return opt;
  };

  const popularGroup = document.createElement('optgroup');
  popularGroup.label = 'Popular Makes';
  popularMakes.forEach((m) => popularGroup.appendChild(makeOption(m)));

  const otherGroup = document.createElement('optgroup');
  otherGroup.label = 'Other Makes';
  otherMakes.forEach((m) => otherGroup.appendChild(makeOption(m)));

  // Preserve the current selection and the leading "All Makes" placeholder.
  const selectedValue = selectEl.value;
  const emptyOption   = selectEl.options[0];

  selectEl.innerHTML = '';
  selectEl.appendChild(emptyOption.cloneNode(true));
  selectEl.appendChild(popularGroup);
  selectEl.appendChild(otherGroup);

  selectEl.value = selectedValue;
}

// SRP make filter: apply Popular Makes grouping after each filter (re)render.
// DSPFilter dispatches 'inventory_search_filter_post_update' (bubbles: true)
// on both initial load and every dynamic filter update.
document.addEventListener('inventory_search_filter_post_update', (e) => {
  const filter = e.detail?.filter;
  if (!filter || filter.searchParameter !== 'make') return;

  const selectEl = filter.container.querySelector('select');
  if (!selectEl) return;

  applyGroupedMakeOptgroups(selectEl, filter.options);
});

// Body Style (category) filter: nest level-2 cab categories under their
// level-1 parent (e.g. Regular/Extended/Crew Cab under Pickup Truck).
// The OEM Checkboxes.js only handles level 0 and level 1, so level-2 items
// end up appended to the root list. This post-processes the DOM to move them.
document.addEventListener('inventory_search_filter_post_update', (e) => {
  const filter = e.detail?.filter;
  if (!filter || filter.searchParameter !== 'category') return;

  const level2Options = (filter.options || []).filter((opt) => opt.level === 2);
  if (!level2Options.length) return;

  level2Options.forEach((opt) => {
    const parentPathItem = opt.path && opt.path[1];
    if (!parentPathItem) return;

    const parentId = parseInt(parentPathItem.split(':')[0], 10);
    if (isNaN(parentId)) return;

    // Find the parent level-1 checkbox and its <li>
    const parentInput = document.querySelector(`#category-${parentId}`);
    if (!parentInput) return;
    const parentLi = parentInput.closest('li.checkbox-tree__node');
    if (!parentLi) return;

    // Create a sub-list under the parent <li> if one doesn't exist yet
    let childList = parentLi.querySelector('ul.checkbox-tree__child--sub');
    if (!childList) {
      childList = document.createElement('ul');
      childList.classList.add('checkbox-tree__child', 'checkbox-tree__child--sub');
      parentLi.appendChild(childList);
      parentLi.classList.add('checkbox-tree__node--has-sub');
    }

    // Move the level-2 <li> into the sub-list
    const childInput = document.querySelector(`#category-${opt.id}`);
    if (!childInput) return;
    const childLi = childInput.closest('li.checkbox-tree__node');
    if (!childLi) return;

    childList.appendChild(childLi);
  });
});

// Set up mini search refresh support for the sale type filter.
let searchMiniWidget = null;
document.addEventListener('inventory_search_mini_setup', (e) => {
  searchMiniWidget = e.searchMini;
  searchMiniWidget.values.sale_type = '';

  // Override fetchMakesAndModels to group makes by popularity
  searchMiniWidget.fetchMakesAndModels = async function () {
    const url = new URL('/inventory/search', window.location.href);
    url.searchParams.set('metadata_only', 'true');
    url.searchParams.append('metadata[]', 'make');
    url.searchParams.append('metadata[]', 'model');

    for (const key in this.values) {
      const value = this.values[key];
      if (value) {
        url.searchParams.append(key, value);
      }
    }

    this.setLoading(this.makeSelect);
    this.setLoading(this.modelSelect);

    const previousModels = this.modelSelect ? this.modelSelect.choices : [];
    const previousMakes = this.makeSelect ? this.makeSelect.choices : [];

    try {
      const response = await fetch(url.toString());
      const data = await response.json();
      const models = data.model;
      const makes = data.make;

      // Set makes with Popular / Other grouping
      if (this.makeSelect) {
        const grouped = buildGroupedMakeChoices(makes, 'All Makes');
        this.makeSelect.setChoices(grouped, 'value', 'label', true);
        this.makeSelect.enable();
      }

      // Set models (same as original plugin behaviour)
      this.removeLoading(
        this.modelSelect,
        [{ slug: '', name: 'All Models' }, ...models],
        'slug',
        'name',
      );

      if (this.modelSelect) {
        this.modelSelect.setChoiceByValue('');
        this.modelSelect.setChoiceByValue(this.values.model);
      }
      if (this.makeSelect) {
        this.makeSelect.setChoiceByValue('');
        this.makeSelect.setChoiceByValue(this.values.make);
      }
    } catch (err) {
      this.removeLoading(this.modelSelect, previousModels);
      this.removeLoading(this.makeSelect, previousMakes);
      console.warn('Unable to fetch inventory makes and models.', err.message);
    }
  };
});

document.addEventListener('DOMContentLoaded', function () {
  const $srpPage = $('.inventory-page');
  const $defaultFilters = $srpPage.find('.filter-layout-default');


  $('.site-search-toggle-btn').on('click', function() {
    const $toggleBtn = $(this);
    
    if ($toggleBtn.hasClass('is-active')) {
      const focusID = $(this).data('focusid');
      $(`#${focusID}`).find('.search__input').trigger('focus');
    } 
    
  });

  if ($srpPage.length && !$defaultFilters.length) {
    const $filterSort = $srpPage.find('.filter-sort');
    const $filterCounts = $srpPage.find('.filter-counts-desktop');
    $filterSort.prependTo($('.filters-exposed'));
    $filterCounts.prependTo($('.filters-exposed'));
  }

  // Update the mini search when the sale type filter changes.
  if (searchMiniWidget) {
    const typeFilter = document.getElementById('inventory_type');
    searchMiniWidget.values.sale_type = typeFilter.value;

    typeFilter.addEventListener('change', (e) => {
      if (searchMiniWidget.values.sale_type !== e.target.value) {
        searchMiniWidget.values.sale_type = e.target.value;
        searchMiniWidget.fetchMakesAndModels();
      }
    });
  }

  function applyMinMaxFilterLabels(id) {
    $(`#${id}.step-numeric`).find('.row').find('.col-6').each(function(index) {
      if (index == 0) {
        $(this).prepend('<label>Min</label>');
      } else {
        $(this).prepend('<label>Max</label>');
      }
    })
  }

  // special model search button
  function applySpecialExternalLink() {
    $('.vehicle').each(function () {
      const $vehicle = $(this);
      const $statusLabelSale = $vehicle.find('.vehicle__footer .status-label');
      const $footerTags = $vehicle.find('.vehicle__footer-tags');
      $statusLabelSale.clone(false, false).appendTo($footerTags);
      $statusLabelSale.remove();
      $footerTags.prependTo($vehicle.find('.vehicle__footer'));
    });
  }

  // garage toggle to vehicle card footer
  function relocateGarageToggle() {
    $('.vehicle').each(function () {
      const $vehicle = $(this);
      const $garageLink = $vehicle.find('.vehicle__title .switch');
      const $vehicleGarageInventory = $vehicle.find('.vehicle__garage-and-inventory');
      $garageLink.clone(true, true).appendTo($vehicleGarageInventory);
      $garageLink.remove();
    });
  }

  applyCardGalleries();
  relocateGarageToggle();
  applyMinMaxFilterLabels('year');
  

  $(document).on('post_render_inventory_search_results', function () {
    applyCardGalleries();
    relocateGarageToggle();
  });

  $("a[href='/garage']").attr('href', '/my-cars');

  const $garagePage = $('.garage-page');
  if ($garagePage.length) {
    $garagePage.find('.page__post-title').text('My Cars');
    $garagePage.find('.page__hero-callout').remove();
    $garagePage.find('.garage-ctas > .garage-page__subtitle').remove();
    $garagePage.find('.garage-ctas .garage-page__mock-toggle').remove();
    $garagePage.find('.garage-ctas .garage-ctas__row .link:last-child').remove();
    $garagePage.find('.garage-ctas .garage-ctas__row .theme-bar:first-child').remove();
    $garagePage.find('.garage-section__no-results .base__title > p').text('My Cars');
  }

  const $vdpPage = $('.vdp-page');
  if ($vdpPage.length) {
    // re-label garage ctas on vdp
    $('.vdp-page__garage').find('.oem-field .field-label').text('Add to My Cars');
    $('.vdp-page__sticker-garage').find('.button .button__text').text('Go to My Cars');
    // update main vdp menu name
    $(`#modal-vdp-menu`).find('.modal__header .iconic__title').text('Contact');
    // vehicle image and nameplate to vdp modal footers
    const $vdpHeading = $vdpPage.find('.vdp-heading__name');
    const $firstImage = $vdpPage.find('.masonry-gallery__grid .masonry-gallery__item:first-child');
    $('.main > .modal .modal__footer').each(function () {
      const $footer = $(this);
      $footer.append($firstImage.clone(true, true));
      $footer.append($vdpHeading.clone(true, true));
    });
  }

  const $samplerTemplatePage = $('.page-template-sampler');

  if ($samplerTemplatePage.length) {
    const brand3DOM = `<div class="flex flex-row flex-wrap gap-4 mb-6">
      <div class="paint-chip">
        <div class="paint-chip__chip bg-brand-3-bright" style="background-color: #79ceff">
            <p class="paint-chip__text text-white">#79ceff</p>
        </div>
        <p class="paint-chip__label">brand-3-bright</p>
      </div>
      <div class="paint-chip">
        <div class="paint-chip__chip bg-brand-3-primary" style="background-color: #38b6ff">
          <p class="paint-chip__text text-white">#38b6ff</p>
        </div>
        <p class="paint-chip__label">brand-3-prime</p>
      </div>
      <div class="paint-chip">
        <div class="paint-chip__chip bg-brand-3-dark" style="background-color: #0d81c3">
          <p class="paint-chip__text text-white">#0d81c3</p>
        </div>
        <p class="paint-chip__label">brand-3-dark</p>
      </div>
    </div>`;

    $('.main .dsp-container-wide:first-child').append(brand3DOM);
  }

  $('.page-template:not(.page-template-home)').find('.header-bottom-independent').remove();


});
